import React, { useState, useEffect } from "react";
import { Box, Typography, IconButton, Alert, LinearProgress } from "@mui/material";
import { Upload, Trash2 } from "lucide-react";

interface FileUploadProps {
  fileSlots: number[]; // Dynamic array for file inputs

  files: (File | null)[];
  uploadProgress: number[];
  onFileSelect: (index: number, file: File | null) => void;
  onFileRemove: (index: number) => void;
}

export default function MultiFileUpload({
  fileSlots,
  files,
  uploadProgress,
  onFileSelect,
  onFileRemove,
}: FileUploadProps) {
  const [filePreviews, setFilePreviews] = useState<(string | null)[]>([
    null,
    null,
    null,
  ]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const previews = files.map((file) => {
      if (!file) return null;

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        return new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string);
          reader.readAsDataURL(file);
        });
      } else if (file.type === "application/pdf") {
        return Promise.resolve(URL.createObjectURL(file));
      }
      return Promise.resolve(null);
    });

    Promise.all(previews).then(setFilePreviews);

    return () => {
      filePreviews.forEach((preview) => {
        if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
      });
    };
  }, [files]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const selectedFile = e.target.files?.[0] || null;

    if (selectedFile) {
      // Check if file is already uploaded in another slot
      const isDuplicate = files.some((file, i) => file?.name === selectedFile.name && i !== index);

      if (isDuplicate) {
        setError("This file is already uploaded in another slot.");
        return;
      }

      setError(null);
      onFileSelect(index, selectedFile);
    }
  };

  const allFilesUploaded = files.every((file) => file !== null);

  return (
    <Box sx={{ mt: 2 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {fileSlots.map((index) => (
        <Box key={index} sx={{ mb: 3 }}>
          {!files[index] ? (
            // Show File Input only if no file is uploaded
            <Box
              sx={{
                border: "1px dashed #000",
                borderRadius: 3,
                p: 3,
                textAlign: "center",
                cursor: "pointer",
              }}
              onClick={() => document.getElementById(`file-input-${index}`)?.click()}
            >
              <input
                type="file"
                id={`file-input-${index}`}
                hidden
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => handleFileInput(e, index)}
              />
              <Upload size={24} className="mx-auto mb-2" />
              <Typography variant="body1" sx={{ mb: 1 }}>
    {index === 0
      ? "Upload Project Charter, Plan & Status"
      : index === 1
      ? "Upload the risks and propose mitigation actions to remove blockers"
      : index === 2
      ? "Upload the clear result KPI/Financial benefits post Successful delivery & closing of projects"
      : `Upload File ${index + 1}`}
  </Typography>
              <Typography variant="caption" color="text.secondary">
                File types: JPEG, JPG, PNG, PDF, PPTX, DOCX, DOC, PPT, XLSX, XLS, XLSM
              </Typography>
            </Box>
          ) : (
            // Show Uploaded File Preview
            <Box sx={{ mt: 2 }}>
              {filePreviews[index] && (
                <Box
                  sx={{
                    width: "100%",
                    height: 180,
                    bgcolor: "#F8F9FA",
                    borderRadius: 1,
                    overflow: "hidden",
                    mb: 2,
                  }}
                >
                  {files[index]?.type.startsWith("image/") ? (
                    <img
                      src={filePreviews[index] as string}
                      alt={files[index]?.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  ) : files[index]?.type === "application/pdf" ? (
                    <iframe
                      src={filePreviews[index] as string}
                      title="PDF preview"
                      style={{
                        width: "100%",
                        height: "100%",
                        border: "none",
                      }}
                    />
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{ textAlign: "center", padding: 2 }}
                    >
                      Preview not available.
                    </Typography>
                  )}
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <Typography variant="body2">{files[index]?.name}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {Math.round((files[index]?.size || 0) / 1024)} KB
                </Typography>
                <IconButton size="small" onClick={() => onFileRemove(index)}>
                  <Trash2 size={16} color="#FF4D4F" />
                </IconButton>
              </Box>
            </Box>
          )}
          
          {uploadProgress[index] > 0 && uploadProgress[index] < 100 && (
            <LinearProgress variant="determinate" value={uploadProgress[index]} />
          )}
        </Box>
      ))}
    </Box>
  );
}
